'use client';

import * as React from 'react';
import { Play } from 'lucide-react';

import { cn } from '@workspace/ui/lib/utils';

type YouTubeProps = {
  videoId: string;
  title?: string;
  className?: string;
};

function extractVideoId(input: string): string {
  if (/^[\w-]{11}$/.test(input)) return input;

  try {
    const url = new URL(input);
    if (url.hostname.includes('youtube.com')) {
      return url.searchParams.get('v') || url.pathname.split('/').pop() || input;
    }
    if (url.hostname === 'youtu.be') {
      return url.pathname.slice(1);
    }
  } catch {
    // Not a valid URL, continue to fallback
  }

  const match = input.match(/([\w-]{11})/);
  return match ? match[1] : input;
}

export function YouTube({ videoId, title, className }: YouTubeProps) {
  const [isPlaying, setIsPlaying] = React.useState(false);
  const id = extractVideoId(videoId);
  const thumbnailUrl = `https://img.youtube.com/vi/${id}/maxresdefault.jpg`;

  if (isPlaying) {
    return (
      <div
        className={cn(
          'my-8 aspect-video w-full overflow-hidden rounded-lg',
          className
        )}
      >
        <iframe
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0`}
          title={title || 'YouTube video player'}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
          className="h-full w-full rounded-lg border-0"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={() => setIsPlaying(true)}
      className={cn(
        'group relative my-8 aspect-video w-full cursor-pointer overflow-hidden rounded-lg bg-black',
        className
      )}
      aria-label={title || 'Play YouTube video'}
    >
      <img
        src={thumbnailUrl}
        alt={title || 'YouTube video thumbnail'}
        className="h-full w-full object-cover transition-opacity group-hover:opacity-75"
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-600 text-white shadow-lg transition-transform group-hover:scale-110">
          <Play className="h-8 w-8 fill-current pl-1" />
        </div>
      </div>
    </button>
  );
}
